import React, { useState, useMemo } from "react";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Layout from "../../components/Layout/Layout";
import {
  Card,
  Button,
  Input,
  Select,
  SearchableSelect,
  Modal,
  Spinner,
} from "../../components/UI";
import {
  leadsAPI,
  usersAPI,
  pipelineAPI,
  productsAPI,
} from "../../services/api";
import toast from "react-hot-toast";
import {
  HiOutlinePlus,
  HiOutlineUser,
  HiOutlinePhone,
  HiOutlineMail,
  HiOutlineUpload,
  HiOutlineDownload,
  HiOutlineUserAdd,
  HiOutlineExclamation,
  HiOutlineCube,
} from "react-icons/hi";
import Papa from "papaparse";
import {
  validateInternationalPhone,
  validateOptionalInternationalPhone,
  PHONE_COUNTRY_OPTIONS,
  getSavedPhoneCountry,
  savePhoneCountry,
} from "../../utils/validation";
import ImportPreview from "./ImportPreview";

// Name validation function
const validateName = (name) => {
  if (!name) return { isValid: false, error: "Name is required" };

  const cleanName = name.trim();

  if (cleanName.length < 2) {
    return { isValid: false, error: "Name must be at least 2 characters" };
  }

  // Name is allowed to contain letters, numbers, spaces, emojis, and
  // special characters (e.g. "John D.", "O'Brien", "Raj 🌟", "Raj2").
  return { isValid: true, cleanName };
};

// Email validation function
const validateEmail = (email) => {
  if (!email) return { isValid: true, cleanEmail: "" }; // Email is optional

  const cleanEmail = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
    return { isValid: false, error: "Invalid email format" };
  }

  return { isValid: true, cleanEmail };
};

// Lead disposition status — matches the backend's full status enum
// (telecallsaas_backend/server.js leadSchema.status). Never hard-fails the
// row; an unrecognized value just falls back to "new" with a warning.
const LEAD_STATUS_VALUES = [
  "new", "open", "in_progress", "follow_up", "cold", "warm", "hot",
  "converted", "closed", "lost", "not_connected",
];
const validateStatus = (status) => {
  if (!status) return { isValid: true, cleanStatus: "new" };
  const normalized = status.trim().toLowerCase().replace(/[\s-]+/g, "_");
  if (!LEAD_STATUS_VALUES.includes(normalized)) {
    return { isValid: true, cleanStatus: "new", warning: `Unrecognized status "${status}" — defaulted to "new"` };
  }
  return { isValid: true, cleanStatus: normalized };
};

// Lead priority — matches the Auto-Assign Rules "Lead Priority" options.
const LEAD_PRIORITY_VALUES = ["urgent", "high", "medium", "low"];
const validatePriority = (priority) => {
  if (!priority) return { isValid: true, cleanPriority: "medium" };
  const normalized = priority.trim().toLowerCase();
  if (!LEAD_PRIORITY_VALUES.includes(normalized)) {
    return { isValid: true, cleanPriority: "medium", warning: `Unrecognized priority "${priority}" — defaulted to "medium"` };
  }
  return { isValid: true, cleanPriority: normalized };
};

const statusOptions = [
  // { value: "new", label: "New" },
  // { value: "open", label: "Open" },
  // { value: "in_progress", label: "In Progress" },
  // { value: "follow_up", label: "Follow Up" },
  { value: "cold", label: "Cold" },
  { value: "warm", label: "Warm" },
  { value: "hot", label: "Hot" },
];

const AssignLeads = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state for single lead
  const [leadForm, setLeadForm] = useState({
    name: "",
    phone: "",
    alternatePhone: "",
    email: "",
    status: "new",
    stage: "",
    notes: "",
    assignedTo: "",
    productIds: [],
  });
  const [formErrors, setFormErrors] = useState({});
  // Country used for phone numbers typed / uploaded without a country code
  const [phoneCountry, setPhoneCountry] = useState(getSavedPhoneCountry);
  const [bulkCountry, setBulkCountry] = useState(getSavedPhoneCountry);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  // Bulk import state
  const [bulkLeads, setBulkLeads] = useState([]);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkAssignTo, setBulkAssignTo] = useState("");
  const [bulkStage, setBulkStage] = useState(""); // default stage applied to imported leads
  const [bulkProductIds, setBulkProductIds] = useState([]); // products applied to imported leads
  const [validationResults, setValidationResults] = useState(null);
  // Bumped on every file upload so the preview's tabs / edits start fresh
  const [bulkUploadId, setBulkUploadId] = useState(0);
  const [isValidating, setIsValidating] = useState(false);

  // Fetch telecallers for assignment
  const { data: telecallersData, isLoading: telecallersLoading } = useQuery({
    queryKey: ["telecallers"],
    queryFn: async () => {
      const response = await usersAPI.getAll({ role: "telecaller" });
      return response.data?.data || [];
    },
  });

  // Fetch all leads for duplicate checking
  const { data: existingLeadsData } = useQuery({
    queryKey: ["allLeadsForValidation"],
    queryFn: async () => {
      const response = await leadsAPI.getAll({ limit: 10000 });
      return response.data?.data || [];
    },
    staleTime: 30000, // Cache for 30 seconds
  });

  // Fetch pipeline stages for the stage selector
  const { data: stagesData } = useQuery({
    queryKey: ["pipelineStages"],
    queryFn: async () => {
      const response = await pipelineAPI.getAll();
      return response.data?.data || [];
    },
  });

  const telecallers = telecallersData || [];
  const stages = stagesData || [];

  // Fetch active products for optional product selection
  const { data: productsData } = useQuery({
    queryKey: ["products", "active"],
    queryFn: async () => {
      const response = await productsAPI.getAll({ active: "true" });
      return response.data?.data || [];
    },
  });
  const products = productsData || [];

  // Stage dropdown options from the organization's pipeline
  const stageOptions = stages.map((stage) => ({
    value: stage.key,
    label: stage.name,
  }));

  // Default stage for a new lead: first pipeline stage, otherwise 'new_lead'
  const defaultStageKey = stages[0]?.key || "new_lead";

  // Get existing phone numbers for duplicate check
  const existingPhones = useMemo(() => {
    if (!existingLeadsData) return new Set();
    return new Set(
      existingLeadsData.map((lead) =>
        lead.phone?.replace(/[\s\-]/g, "").replace(/^\+91/, ""),
      ),
    );
  }, [existingLeadsData]);

  // Get existing alternate phone numbers for duplicate check
  const existingAlternatePhones = useMemo(() => {
    if (!existingLeadsData) return new Set();
    return new Set(
      existingLeadsData
        .map((lead) =>
          lead.alternatePhone?.replace(/[\s\-]/g, "").replace(/^\+91/, ""),
        )
        .filter(Boolean),
    );
  }, [existingLeadsData]);

  // Create lead mutation
  const createMutation = useMutation({
    mutationFn: (data) => leadsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["leadsCounts"]);
      toast.success("Lead created successfully");
      navigate("/app/leads");
    },
    onError: (error) => {
      const responseData = error.response?.data;
      if (responseData?.code === 'LIMIT_EXCEEDED') {
        toast.error(responseData.message || 'Contact limit reached. Please upgrade your plan.');
      } else {
        toast.error('Failed to create lead');
      }
    },
  });

  // Bulk create mutation
  const bulkCreateMutation = useMutation({
    mutationFn: (leads) => leadsAPI.bulkCreate(leads),
    onSuccess: (response) => {
      queryClient.invalidateQueries(["leads"]);
      queryClient.invalidateQueries(["leadsCounts"]);
      const { totalImported, totalSkipped, duplicates } =
        response.data?.data || {};
      console.log("Bulk create response:", response.data);
      let message = `${response.data.message || "Leads imported successfully"}.`;
      if (totalSkipped > 0 || duplicates > 0) {
        message += `. ${duplicates || 0} duplicates skipped`;
      }
      toast.success(message);
      navigate("/app/leads");
    },
    onError: (error) => {
      const responseData = error.response?.data;
      if (responseData?.code === 'LIMIT_EXCEEDED') {
        toast.error(responseData.message || 'Contact limit reached. Please upgrade your plan.');
      } else {
        const errorMsg =
          responseData?.message || "Failed to create leads";
        toast.error(errorMsg);
      }
    },
  });

  const handleCreateLead = async () => {
    const errors = {};

    // Validate name
    const nameValidation = validateName(leadForm.name);
    if (!nameValidation.isValid) {
      errors.name = nameValidation.error;
    }

    // Phone: validated against the picked country's numbering plan
    const phoneValidation = validateInternationalPhone(leadForm.phone, phoneCountry);

    if (!phoneValidation.isValid) {
      errors.phone = phoneValidation.error;
    }
    // Validate alternatePhone (optional)
    const alternatePhoneValidation = validateOptionalInternationalPhone(leadForm.alternatePhone, phoneCountry);
    if (!alternatePhoneValidation.isValid) {
      errors.alternatePhone = alternatePhoneValidation.error;
    }

    // Validate email (optional)
    const emailValidation = validateEmail(leadForm.email);

    if (!emailValidation.isValid) {
      errors.email = emailValidation.error;
    }

    // Email: optional, no format validation

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setFormErrors({});
    createMutation.mutate({
    ...leadForm,
    stage: leadForm.stage || defaultStageKey,
    phone: phoneValidation.cleanPhone,
    alternatePhone: alternatePhoneValidation.cleanPhone,
    name: nameValidation.cleanName,
    email: emailValidation.cleanEmail,
    defaultCountry: phoneCountry,
    });
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    // Prevent numbers and enforce 100 character limit
    if (/\d/.test(value)) return;
    if (value.length > 100) return;
    setLeadForm({ ...leadForm, name: value });
    if (formErrors.name) setFormErrors({ ...formErrors, name: "" });
  };

  // Phone inputs accept digits only (plus an optional leading "+" for the
  // country code); letters, spaces and symbols are stripped as you type and
  // the length is capped at 15 digits (E.164 max).
  const toPhoneDigits = (raw) => {
    const str = String(raw || "");
    const hasPlus = str.trimStart().startsWith("+");
    const digits = str.replace(/\D/g, "").slice(0, 15);
    return (hasPlus ? "+" : "") + digits;
  };

  const handlePhoneChange = (e) => {
    const value = toPhoneDigits(e.target.value);
    setLeadForm({ ...leadForm, phone: value });
    if (formErrors.phone) setFormErrors({ ...formErrors, phone: "" });
  };

  const handleAlternatePhoneChange = (e) => {
    const value = toPhoneDigits(e.target.value);
    setLeadForm({ ...leadForm, alternatePhone: value });
    if (formErrors.alternatePhone) setFormErrors({ ...formErrors, alternatePhone: "" });
  };

  const handleEmailChange = (e) => {
    // Only allow valid email characters: letters, digits, @ . _ - +
    const value = e.target.value.replace(/[^a-zA-Z0-9@._\-+]/g, "");
    setLeadForm({ ...leadForm, email: value });
    if (formErrors.email) setFormErrors({ ...formErrors, email: "" });
  };

  // Validates bulk rows (name, phone per country, email, duplicates in file /
  // in system). Used on upload and again after edits in the preview.
  const validateBulkRows = (rows, defaultCountry = bulkCountry) => {
    // Validate each lead
    const validatedLeads = rows.map((row) => {
      // Work on a copy; rawPhone / rawAlternatePhone keep the original (or
      // edited) values so re-validation never runs on a normalized number
      const lead = { ...row };
      const rowCountry = lead.phoneCountry || defaultCountry;
      const errors = [];
      const warnings = [];

      // Validate name
      const nameValidation = validateName(lead.name);
      if (!nameValidation.isValid) {
        errors.push(`Name: ${nameValidation.error}`);
      }

      // Validate phone
      const phoneValidation = validateInternationalPhone(lead.rawPhone, rowCountry);
      lead.phone = lead.rawPhone;
      if (!phoneValidation.isValid) {
        errors.push(`Phone: ${phoneValidation.error}`);
      } else {
        lead.phone = phoneValidation.cleanPhone;
      }

      // Validate alternatePhone (optional)
      const alternatePhoneValidation = validateOptionalInternationalPhone(lead.rawAlternatePhone, rowCountry);
      lead.alternatePhone = lead.rawAlternatePhone;
      if (!alternatePhoneValidation.isValid) {
        errors.push(`Alternate Phone: ${alternatePhoneValidation.error}`);
      } else {
        lead.alternatePhone = alternatePhoneValidation.cleanPhone;
      }

      // Validate email
      const emailValidation = validateEmail(lead.email);
      if (!emailValidation.isValid) {
        errors.push(`Email: ${emailValidation.error}`);
      } else {
        lead.email = emailValidation.cleanEmail;
      }

      // Status/priority never hard-fail the row — unrecognized values
      // just fall back to a sensible default, surfaced as a warning.
      const statusValidation = validateStatus(lead.status);
      lead.status = statusValidation.cleanStatus;
      if (statusValidation.warning) warnings.push(statusValidation.warning);
      const priorityValidation = validatePriority(lead.priority);
      lead.priority = priorityValidation.cleanPriority;
      if (priorityValidation.warning) warnings.push(priorityValidation.warning);
      lead.source = lead.source || undefined;

      // Check for duplicates within file
      lead.isDuplicateInFile = false;
      lead.isDuplicateInSystem = false;

      return {
        ...lead,
        errors,
        warnings,
        isValid: errors.length === 0,
        name: nameValidation.isValid ? nameValidation.cleanName : lead.name,
      };
    });

    // Check for duplicates within file
    const phoneCounts = {};
    validatedLeads.forEach((lead) => {
      if (lead.phone) {
        phoneCounts[lead.phone] = (phoneCounts[lead.phone] || 0) + 1;
      }
      if (lead.alternatePhone) {
        phoneCounts[lead.alternatePhone] = (phoneCounts[lead.alternatePhone] || 0) + 1;
      }
    });

    // Mark duplicates within file
    validatedLeads.forEach((lead) => {
      if (lead.phone && phoneCounts[lead.phone] > 1) {
        lead.isDuplicateInFile = true;
        lead.isValid = false;
        if (!lead.errors.includes("Duplicate phone in file")) {
          lead.errors.push("Duplicate phone in file");
        }
      }
      if (lead.alternatePhone && phoneCounts[lead.alternatePhone] > 1) {
        lead.isDuplicateInFile = true;
        lead.isValid = false;
        if (!lead.errors.includes("Duplicate alternate phone in file")) {
          lead.errors.push("Duplicate alternate phone in file");
        }
      }
    });

    // Check for duplicates in system
    validatedLeads.forEach((lead) => {
      if (lead.phone && existingPhones.has(lead.phone)) {
        lead.isDuplicateInSystem = true;
        lead.isValid = false;
        if (!lead.errors.includes("Phone already exists in system")) {
          lead.errors.push("Phone already exists in system");
        }
      }
      // Check if alternatePhone matches an existing phone or alternatePhone
      if (lead.alternatePhone) {
        if (existingPhones.has(lead.alternatePhone) || existingAlternatePhones.has(lead.alternatePhone)) {
          lead.isDuplicateInSystem = true;
          lead.isValid = false;
          if (!lead.errors.includes("Alternate phone already exists in system")) {
            lead.errors.push("Alternate phone already exists in system");
          }
        }
      }
      // Check if phone matches an existing alternatePhone
      if (lead.phone && existingAlternatePhones.has(lead.phone)) {
        lead.isDuplicateInSystem = true;
        lead.isValid = false;
        if (!lead.errors.includes("Phone already exists as alternate phone in system")) {
          lead.errors.push("Phone already exists as alternate phone in system");
        }
      }
    });


    return validatedLeads;
  };

  // Validates rows and stores them + the summary; returns the validated rows
  const applyBulkValidation = (rows, defaultCountry = bulkCountry) => {
    const validatedLeads = validateBulkRows(rows, defaultCountry);
    setBulkLeads(validatedLeads);
    const validCount = validatedLeads.filter((l) => l.isValid).length;
    const invalidCount = validatedLeads.length - validCount;
    setValidationResults(
      invalidCount > 0
        ? {
            total: validatedLeads.length,
            valid: validCount,
            invalid: invalidCount,
            duplicates: validatedLeads.filter((l) => l.isDuplicateInSystem).length,
            duplicateInFile: validatedLeads.filter((l) => l.isDuplicateInFile).length,
          }
        : null,
    );
    return validatedLeads;
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const isExcel = /\.(xlsx|xls)$/i.test(file.name);

    const processRows = (rawRows) => {
        const parsed = rawRows
          .map((row, index) => ({
            rowNum: index + 2, // Row number in the file (accounting for header)
            name: row.name || row.Name || "",
            phone: row.phone || row.Phone || row.mobile || row.Mobile || "",
            alternatePhone: row.alternatePhone || row["Alternate Phone"] || row.alternate_phone || row.secondaryPhone || row["Secondary Phone"] || row.secondary_phone || "",
            email: row.email || row.Email || "",
            // A per-row "stage" column overrides the "Default Stage for
            // Imported Leads" selector; otherwise that selector applies.
            stage: row.stage || row.Stage || row.STAGE || bulkStage || defaultStageKey,
            status: row.status || row.Status || row.STATUS || "new",
            source: row.source || row.Source || row.SOURCE || "",
            priority: row.priority || row.Priority || row.PRIORITY || "",
            notes: row.notes || row.Notes || "",
            rawPhone: row.phone || row.Phone || row.mobile || row.Mobile || "",
            rawAlternatePhone: row.alternatePhone || row["Alternate Phone"] || row.alternate_phone || row.secondaryPhone || row["Secondary Phone"] || row.secondary_phone || "",
          }))
          .filter((lead) => lead.name || lead.phone);

        if (parsed.length === 0) {
          toast.error("No valid leads found in file");
          return;
        }

        const validatedLeads = applyBulkValidation(parsed);
        setBulkUploadId((n) => n + 1);
        setShowBulkModal(true);

        const invalidCount = validatedLeads.filter((l) => !l.isValid).length;
        if (invalidCount > 0) {
          toast.error(
            `Found ${invalidCount} invalid/duplicate leads. Fix them in the "Invalid" tab or import only the valid ones.`,
          );
        } else {
          toast.success(`Found ${validatedLeads.length} valid leads ready to import`);
        }
    };

    if (isExcel) {
      const reader = new FileReader();
      reader.onload = (event) => {
        const workbook = XLSX.read(event.target.result, { type: "array" });
        // Prefer the "Leads Template" sheet; skip the "Valid Values" reference
        // sheet if this is our own downloaded template re-uploaded as-is.
        const sheetName =
          workbook.SheetNames.find((n) => n.toLowerCase() === "leads template") ||
          workbook.SheetNames.find((n) => n.toLowerCase() !== "valid values") ||
          workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        // raw: false keeps phone numbers as their displayed text, never as JS numbers
        processRows(XLSX.utils.sheet_to_json(sheet, { defval: "", raw: false }));
      };
      reader.readAsArrayBuffer(file);
    } else {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => processRows(results.data),
        error: () => toast.error("Failed to parse file"),
      });
    }
  };

  const handleBulkCreate = async () => {
    if (bulkLeads.length === 0) return;

    // Only import valid leads
    const validLeads = bulkLeads.filter((lead) => lead.isValid);

    if (validLeads.length === 0) {
      toast.error("No valid leads to import. Please fix errors and try again.");
      return;
    }

    // Format leads for API
    const leadsToCreate = validLeads.map((lead) => ({
      name: lead.name,
      phone: lead.phone,
      alternatePhone: lead.alternatePhone || undefined,
      email: lead.email,
      stage: lead.stage || bulkStage || defaultStageKey,
      status: lead.status || "new",
      source: lead.source || undefined,
      priority: lead.priority || "medium",
      notes: lead.notes,
      assignedTo: bulkAssignTo || undefined,
      productIds: bulkProductIds.length > 0 ? bulkProductIds : undefined,
    }));

    bulkCreateMutation.mutate({ leads: leadsToCreate });
  };

  // Downloads a real .xlsx workbook (not CSV) so phone numbers stay text (no
  // Excel scientific-notation like 9.877E+09) and there's a second sheet
  // listing every valid stage/status/priority/source value.
  const handleDownloadTemplate = () => {
    const headers = ["name", "phone", "alternatePhone", "email", "stage", "status", "source", "priority", "notes"];
    const exampleRows = [
      ["John Doe", "9876543210", "9876543211", "john@example.com", "new_lead", "new", "Website", "high", "Interested in a demo"],
      ["Jane Smith", "9876543211", "", "jane@example.com", "connected", "warm", "Referral", "medium", ""],
    ];

    const templateSheet = XLSX.utils.aoa_to_sheet([headers, ...exampleRows]);
    // Force the phone / alternatePhone columns (B, C) to text so Excel never
    // converts long numbers to scientific notation.
    exampleRows.forEach((_, rowIdx) => {
      ["B", "C"].forEach((col) => {
        const ref = `${col}${rowIdx + 2}`; // +2: row 1 is the header
        if (templateSheet[ref]) {
          templateSheet[ref].t = "s";
          templateSheet[ref].z = "@";
        }
      });
    });
    templateSheet["!cols"] = [
      { wch: 16 }, { wch: 14 }, { wch: 16 }, { wch: 22 },
      { wch: 20 }, { wch: 12 }, { wch: 14 }, { wch: 10 }, { wch: 30 },
    ];

    const referenceRows = [
      ["Field", "Value", "Meaning"],
      ["Stage", "new_lead", "New Lead (default if left blank)"],
      ["Stage", "contact_attempted", "Contact Attempted"],
      ["Stage", "not_connected", "Not Connected"],
      ["Stage", "connected", "Connected"],
      ["Stage", "qualified", "Qualified"],
      ["Stage", "follow_up_required", "Follow-Up Required"],
      ["Stage", "meeting_scheduled", "Meeting Scheduled"],
      ["Stage", "proposal_sent", "Proposal Sent"],
      ["Stage", "negotiation", "Negotiation"],
      ["Stage", "converted", "Converted"],
      ["Stage", "lost", "Lost"],
      ["Stage", "not_interested", "Not Interested"],
      ["Status", "new", "New (default if left blank)"],
      ["Status", "open", "Open"],
      ["Status", "in_progress", "In Progress"],
      ["Status", "follow_up", "Follow Up"],
      ["Status", "cold", "Cold"],
      ["Status", "warm", "Warm"],
      ["Status", "hot", "Hot"],
      ["Status", "converted", "Converted"],
      ["Status", "closed", "Closed"],
      ["Status", "lost", "Lost"],
      ["Status", "not_connected", "Not Connected"],
      ["Priority", "urgent", "Urgent"],
      ["Priority", "high", "High"],
      ["Priority", "medium", "Medium (default if left blank)"],
      ["Priority", "low", "Low"],
      ["Source", "Website", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Referral", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Cold Call", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "LinkedIn", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Email Campaign", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Social Media", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Advertisement", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Walk-in", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Phone Inquiry", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "Other", "Matches Leads → Auto-Assign Rules by source"],
      ["Source", "(any other text)", "Accepted, but won't match an Auto-Assign Rule by source"],
    ];
    const referenceSheet = XLSX.utils.aoa_to_sheet(referenceRows);
    referenceSheet["!cols"] = [{ wch: 12 }, { wch: 22 }, { wch: 48 }];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, templateSheet, "Leads Template");
    XLSX.utils.book_append_sheet(workbook, referenceSheet, "Valid Values");

    XLSX.writeFile(workbook, "leads-import-template.xlsx");
  };

  return (
    <Layout>
      <div className="p-4 lg:p-6 space-y-6">
        {/* Header — stacks on small screens so title and button never squeeze */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-slate-100">
              Add & Assign Leads
            </h1>
            <p className="text-gray-500 dark:text-slate-400 text-sm mt-1">
              Create individual leads or import in bulk
            </p>
          </div>
          <Button
            variant="outline"
            className="w-full sm:w-auto shrink-0"
            onClick={() => navigate("/app/leads")}
          >
            View All Leads
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Single Lead Form */}
          <Card padding="p-6">
            <h2 className="font-semibold text-gray-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <HiOutlineUser className="w-5 h-5" />
              Add Single Lead
            </h2>
            <div className="space-y-4">
              <Input
                label={
                  <>
                    Name <span className="text-red-500 dark:text-red-400">*</span>
                  </>
                }
                value={leadForm.name}
                onChange={handleNameChange}
                placeholder="Enter lead name"
                error={formErrors.name}
                maxLength={100}
                rightIcon={<span className="text-xs text-gray-400 dark:text-slate-500">{leadForm.name.length}/100</span>}
              />
              <SearchableSelect
                label="Country"
                searchPlaceholder="Search country or code (e.g. UAE, 971)"
                options={PHONE_COUNTRY_OPTIONS}
                value={phoneCountry}
                onChange={(e) => {
                  setPhoneCountry(e.target.value);
                  savePhoneCountry(e.target.value);
                  if (formErrors.phone || formErrors.alternatePhone) {
                    setFormErrors({ ...formErrors, phone: "", alternatePhone: "" });
                  }
                }}
              />
              <Input
                label={
                  <>
                    Phone <span className="text-red-500 dark:text-red-400">*</span>
                  </>
                }
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                maxLength={16}
                value={leadForm.phone}
                onChange={handlePhoneChange}
                placeholder="Phone number (country code optional)"
                icon={<HiOutlinePhone className="w-5 h-5" />}
                error={formErrors.phone}
              />
              <Input
                label="Alternate Phone"
                type="tel"
                inputMode="tel"
                maxLength={16}
                value={leadForm.alternatePhone}
                onChange={handleAlternatePhoneChange}
                placeholder="Enter alternate phone (optional)"
                icon={<HiOutlinePhone className="w-5 h-5" />}
                error={formErrors.alternatePhone}
              />
              <Input
                label="Email"
                type="email"
                value={leadForm.email}
                onChange={handleEmailChange}
                placeholder="Enter email (optional)"
                icon={<HiOutlineMail className="w-5 h-5" />}
                error={formErrors.email}
              />
              {/* Stage & Status stack on very small screens */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Select
                  label="Stage"
                  options={stageOptions}
                  value={leadForm.stage || defaultStageKey}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, stage: e.target.value })
                  }
                />
                <Select
                  label="Status"
                  options={statusOptions}
                  value={leadForm.status}
                  onChange={(e) =>
                    setLeadForm({ ...leadForm, status: e.target.value })
                  }
                />
              </div>

              {/* Assign To Telecaller */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5 flex items-center gap-1">
                  <HiOutlineUserAdd className="w-4 h-4" />
                  Assign to Telecaller
                </label>
                {telecallersLoading ? (
                  <div className="text-sm text-gray-500 dark:text-slate-400 py-2">
                    Loading telecallers...
                  </div>
                ) : (
                  <Select
                    options={[
                      { value: "", label: "Leave unassigned" },
                      ...telecallers.map((u) => ({
                        value: u._id,
                        label: `${u.firstName} ${u.lastName}`,
                      })),
                    ]}
                    value={leadForm.assignedTo}
                    onChange={(e) =>
                      setLeadForm({ ...leadForm, assignedTo: e.target.value })
                    }
                    placeholder="Select telecaller (optional)"
                  />
                )}
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Optionally assign this lead to a telecaller now, or leave
                  unassigned
                </p>
              </div>

              {/* Product selection (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5 flex items-center gap-1">
                  <HiOutlineCube className="w-4 h-4" />
                  Products
                </label>
                {products.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-slate-400">
                    No products available (optional)
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {products.map((product) => {
                      const selected = leadForm.productIds.includes(
                        product._id,
                      );
                      return (
                        <button
                          type="button"
                          key={product._id}
                          onClick={() =>
                            setLeadForm((prev) => ({
                              ...prev,
                              productIds: selected
                                ? prev.productIds.filter(
                                    (id) => id !== product._id,
                                  )
                                : [...prev.productIds, product._id],
                            }))
                          }
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-sm font-medium transition-all ${
                            selected
                              ? "bg-primary-50 border-primary-400 text-primary-700 dark:bg-primary-500/15 dark:border-primary-500/60 dark:text-primary-300"
                              : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-300 dark:hover:border-slate-500"
                          }`}
                        >
                          <HiOutlineCube className="w-4 h-4" />
                          {product.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Optionally link this lead to one or more products
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  Notes
                </label>
                <div className="relative">
                <textarea
                  className="w-full px-4 py-3 rounded-xl border-2 border-gray-200 focus:border-primary-500 focus:ring-0 focus:outline-none dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:border-primary-400"
                  rows={3}
                  maxLength={1000}
                  value={leadForm.notes}
                  onChange={(e) => {
                    if (e.target.value.length > 1000) return;
                    setLeadForm({ ...leadForm, notes: e.target.value });
                  }}
                  placeholder="Add notes..."
                />
                <span className="absolute bottom-2 right-3 text-xs text-gray-400 dark:text-slate-500">{leadForm.notes.length}/1000</span>
                </div>
              </div>
              <Button
                variant="primary"
                className="w-full"
                onClick={handleCreateLead}
                loading={createMutation.isPending || isCheckingDuplicate}
              >
                {/* <HiOutlinePlus className="w-5 h-5 mr-2" /> */}
                {isCheckingDuplicate ? "Checking..." : "Create Lead"}
              </Button>
            </div>
          </Card>

          {/* Bulk Import */}
          <Card padding="p-6">
            <h2 className="font-semibold text-gray-900 dark:text-slate-100 mb-4 flex items-center gap-2">
              <HiOutlineUpload className="w-5 h-5" />
              Bulk Import
            </h2>
            <div className="space-y-4">
              {/* File Upload Area */}
              <div className="border-2 border-dashed border-gray-200 dark:border-slate-600 rounded-xl p-8 text-center hover:border-primary-500 dark:hover:border-primary-400 transition-colors">
                <input
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="csv-upload"
                />
                <label htmlFor="csv-upload" className="cursor-pointer">
                  <HiOutlineUpload className="w-12 h-12 mx-auto text-gray-400 dark:text-slate-500 mb-4" />
                  <p className="text-gray-600 dark:text-slate-300 font-medium">Upload CSV or Excel File</p>
                  <p className="text-sm text-gray-400 dark:text-slate-500 mt-1">
                    Click to browse or drag and drop
                  </p>
                </label>
              </div>

              {/* Country for uploaded numbers without a country code */}
              <div>
                <SearchableSelect
                  label="Phone Number Country"
                  searchPlaceholder="Search country or code (e.g. UAE, 971)"
                  options={PHONE_COUNTRY_OPTIONS}
                  value={bulkCountry}
                  onChange={(e) => {
                    setBulkCountry(e.target.value);
                    savePhoneCountry(e.target.value);
                  }}
                />
                <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">
                  Choose before uploading. Used for numbers without a country code; numbers starting with + are checked against their own country.
                </p>
              </div>

              {/* Default stage for imported leads */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  Default Stage for Imported Leads
                </label>
                <Select
                  options={stageOptions}
                  value={bulkStage || defaultStageKey}
                  onChange={(e) => setBulkStage(e.target.value)}
                />
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Applied to every imported lead unless the CSV has a{" "}
                  <strong>stage</strong> column that overrides it
                </p>
              </div>

              {/* Products for imported leads (optional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5 flex items-center gap-1">
                  <HiOutlineCube className="w-4 h-4" />
                  Products for Imported Leads (Optional)
                </label>
                {products.length === 0 ? (
                  <p className="text-xs text-gray-500 dark:text-slate-400">No products available</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {products.map((product) => {
                      const selected = bulkProductIds.includes(product._id);
                      return (
                        <button
                          type="button"
                          key={product._id}
                          onClick={() =>
                            setBulkProductIds((prev) =>
                              selected
                                ? prev.filter((id) => id !== product._id)
                                : [...prev, product._id],
                            )
                          }
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border-2 text-sm font-medium transition-all ${
                            selected
                              ? "bg-primary-50 border-primary-400 text-primary-700 dark:bg-primary-500/15 dark:border-primary-500/60 dark:text-primary-300"
                              : "bg-white border-gray-200 text-gray-600 hover:border-gray-300 dark:bg-slate-800/60 dark:border-slate-600 dark:text-slate-300 dark:hover:border-slate-500"
                          }`}
                        >
                          <HiOutlineCube className="w-4 h-4" />
                          {product.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Selected products are applied to every imported lead
                </p>
              </div>

              {/* Template Download */}
              <Button
                variant="outline"
                className="w-full"
                icon={<HiOutlineDownload className="w-5 h-5" />}
                onClick={handleDownloadTemplate}
              >
                Download Excel Template
              </Button>

              {/* Instructions — File Format section, violet/indigo colour combination */}
              <div className="bg-indigo-50/70 border border-indigo-100 dark:bg-indigo-500/10 dark:border-indigo-500/25 rounded-xl p-4">
                <h3 className="font-semibold text-indigo-800 dark:text-indigo-300 mb-2 flex items-center gap-1.5">
                  <HiOutlineCube className="w-4 h-4" />
                  File Format
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                  Required columns:{" "}
                  <strong className="text-indigo-700 dark:text-indigo-300">name</strong>,{" "}
                  <strong className="text-indigo-700 dark:text-indigo-300">phone</strong>
                </p>
                <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
                  Optional columns:{" "}
                  <strong className="text-indigo-700 dark:text-indigo-300">alternatePhone</strong>,{" "}
                  <strong className="text-indigo-700 dark:text-indigo-300">email</strong>,{" "}
                  <strong className="text-indigo-700 dark:text-indigo-300">stage</strong>,{" "}
                  <strong className="text-indigo-700 dark:text-indigo-300">status</strong>,{" "}
                  <strong className="text-indigo-700 dark:text-indigo-300">source</strong>,{" "}
                  <strong className="text-indigo-700 dark:text-indigo-300">priority</strong>,{" "}
                  <strong className="text-indigo-700 dark:text-indigo-300">notes</strong>
                </p>
                <div className="text-xs text-slate-600 dark:text-slate-400 mb-2 space-y-1">
                  <p>
                    <strong className="text-indigo-600 dark:text-indigo-300">Tip:</strong> the downloaded template also has a
                    second sheet — "Valid Values" — listing every accepted Stage, Status, Priority and Source value.
                  </p>
                  <p>
                    <strong className="text-indigo-600 dark:text-indigo-300">Stage:</strong> a per-row{" "}
                    <strong className="text-indigo-600 dark:text-indigo-300">stage</strong> column overrides the{" "}
                    <strong className="text-indigo-600 dark:text-indigo-300">Default Stage for Imported Leads</strong> selector
                    above; leave it blank to just use that selector for every row.
                  </p>
                  <p>
                    <strong className="text-indigo-600 dark:text-indigo-300">Status values:</strong> new, open, in_progress,
                    follow_up, cold, warm, hot, converted, closed, lost, not_connected. Leave blank to default to "new".
                  </p>
                  <p>
                    <strong className="text-indigo-600 dark:text-indigo-300">Priority:</strong> urgent, high, medium, low
                    (matches Leads → Auto-Assign Rules). Leave blank to default to "medium".
                  </p>
                  <p>
                    <strong className="text-indigo-600 dark:text-indigo-300">Source:</strong> any text describing where the lead
                    came from. To match your Auto-Assign Rules by source, use one of: Website, Referral, Cold Call, LinkedIn,
                    Email Campaign, Social Media, Advertisement, Walk-in, Phone Inquiry, Other.
                  </p>
                  <p>
                    <strong className="text-indigo-600 dark:text-indigo-300">Products:</strong> set using the selector above (not
                    a file column).
                  </p>
                </div>
                {/* Sample — theme-matched code block: light lavender card in
                    light mode, violet-navy "code editor" surface in dark mode
                    (token colours enforced by the html.dark .csv-sample rules
                    in index.css) */}
                <div className="csv-sample mt-3 rounded-lg border border-indigo-200 bg-indigo-50/80 shadow-inner overflow-hidden dark:border-[#2a3158] dark:bg-[#121832] dark:shadow-none">
                  <div className="flex items-center gap-1.5 px-3 pt-2.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-red-400/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-amber-400/80" />
                    <span className="w-2.5 h-2.5 rounded-full bg-green-400/80" />
                    <span className="csv-file ml-2 text-[10px] font-mono uppercase tracking-wider text-indigo-500 dark:text-indigo-300/70">
                      leads-import-template.xlsx — Sheet 1: Leads Template
                    </span>
                  </div>
                  <pre className="px-3 pb-3 pt-1.5 overflow-x-auto font-mono text-xs leading-relaxed">
                    <code>
                      <span className="csv-h text-indigo-700 dark:text-sky-300">
                        name,phone,alternatePhone,email,stage,status,source,priority,notes
                      </span>
                      {"\n"}
                      <span className="csv-name text-emerald-700 dark:text-emerald-300">John Doe,</span>
                      <span className="csv-phone text-slate-700 dark:text-slate-200">9876543210,</span>
                      <span className="csv-phone text-emerald-700 dark:text-emerald-300">9876543211,</span>
                      <span className="csv-email text-amber-700 dark:text-amber-200">john@example.com,</span>
                      <span className="csv-status text-slate-700 dark:text-slate-200">new_lead,new,Website,high,</span>
                      <span className="csv-note text-rose-700 dark:text-rose-300">
                        Interested in a demo
                      </span>
                    </code>
                  </pre>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </div>

      {/* Bulk Preview Modal */}
      <Modal
        isOpen={showBulkModal}
        onClose={() => setShowBulkModal(false)}
        title={`Preview Leads (${bulkLeads.length})`}
        size="lg"
      >
        <div className="space-y-4">
          {/* Assign All Bulk Leads */}
          <div className="bg-blue-50 dark:bg-sky-500/10 border border-blue-200 dark:border-sky-500/30 rounded-xl p-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1.5 flex items-center gap-1">
              <HiOutlineUserAdd className="w-4 h-4" />
              Assign All To (Optional)
            </label>
            <Select
              options={[
                { value: "", label: "Leave all unassigned" },
                ...telecallers.map((u) => ({
                  value: u._id,
                  label: `${u.firstName} ${u.lastName}`,
                })),
              ]}
              value={bulkAssignTo}
              onChange={(e) => {
                const telecallerId = e.target.value;
                setBulkAssignTo(telecallerId);
                // Update all bulk leads with the selected telecaller
                if (telecallerId) {
                  setBulkLeads((prev) =>
                    prev.map((lead) => ({ ...lead, assignedTo: telecallerId })),
                  );
                }
              }}
              placeholder="Select telecaller to assign all leads"
            />
          </div>

          {/* Validation Summary */}
          {validationResults && (
            <div className="bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300 font-medium mb-2">
                <HiOutlineExclamation className="w-5 h-5" />
                <span>Validation Summary</span>
              </div>
              <div className="text-sm text-amber-600 dark:text-amber-300 space-y-1">
                <p>
                  Total leads: <strong>{validationResults.total}</strong>
                </p>
                <p className="text-green-600 dark:text-emerald-400">
                  Valid leads: <strong>{validationResults.valid}</strong>
                </p>
                {validationResults.duplicateInFile > 0 && (
                  <p className="text-red-600 dark:text-red-400">
                    Duplicates within file:{" "}
                    <strong>{validationResults.duplicateInFile}</strong>
                  </p>
                )}
                {validationResults.duplicates > 0 && (
                  <p className="text-red-600 dark:text-red-400">
                    Already in system:{" "}
                    <strong>{validationResults.duplicates}</strong>
                  </p>
                )}
                <p className="text-amber-600">
                  Invalid leads: <strong>{validationResults.invalid}</strong>
                </p>
              </div>
            </div>
          )}

          {/* Preview — Invalid / Valid tabs with inline + bulk edit */}
          {bulkLeads.length > 0 && (
            <ImportPreview
              key={bulkUploadId}
              rows={bulkLeads}
              onRowsChange={(rows) => applyBulkValidation(rows)}
            />
          )}
        </div>
        {/* Footer buttons stack full-width on small screens */}
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4 border-t border-gray-100 mt-4">
          <Button
            variant="ghost"
            className="w-full sm:w-auto"
            onClick={() => setShowBulkModal(false)}
          >
            Cancel
          </Button>
          {validationResults && validationResults.invalid > 0 && (
            <Button
              variant="outline"
              className="w-full sm:w-auto"
              onClick={handleBulkCreate}
              loading={bulkCreateMutation.isPending}
            >
              Import {validationResults.valid} Valid Leads
            </Button>
          )}
          <Button
            variant="primary"
            className="w-full sm:w-auto"
            onClick={handleBulkCreate}
            loading={bulkCreateMutation.isPending}
            disabled={bulkLeads.filter((l) => l.isValid).length === 0}
          >
            {validationResults && validationResults.invalid > 0
              ? `Import ${validationResults.valid} Valid Leads`
              : `Create ${bulkLeads.length} Leads`}
          </Button>
        </div>
      </Modal>
    </Layout>
  );
};

export default AssignLeads;
